/**
 * Popup Type-Specific Handlers
 * Provides specific handling functions for different types of popups
 */
(function () {
  // Dependency check
  const dependencies = ["PopupEventBus", "PopupStore", "PopupDOM"];
  for (const dep of dependencies) {
    if (!window._erj_[dep]) {
      console.error(`${dep} unavailable, popup handler initialization failed`);
      return;
    }
  }

  const _loadConfiguration = () => {
    try {
      const configElement = document.getElementById("popup-configuration-data");
      if (configElement) {
        const config = JSON.parse(configElement.textContent);
        return config;
      }
      return null;
    } catch (e) {
      return null;
    }
  };

  const ERR_MSG = _loadConfiguration()?.err_msg ?? {
    email_validation: "Please enter a valid email address.",
    subscribe_timeout: "Request timed out. Please try again later.",
    subscribe_failed: "Subscription failed. Please try again later.",
  };

  // Define debounce function
  const _debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  // Email validation function
  const _validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const _getPopupEle = (popupId) => {
    return document.querySelector(`[data-erj-popup-id="${popupId}"]`);
  };

  // Display error message
  const _showError = (popupId, message) => {
    const popup = _getPopupEle(popupId);
    if (!popup) return;

    const errorElement = popup.querySelector(".erj-popup-error");
    if (!errorElement) return;

    errorElement.textContent = message;
    errorElement.style.display = "block";
  };

  // Hide error message
  const _hideError = (popupId) => {
    const popup = _getPopupEle(popupId);
    if (!popup) return;

    const errorElement = popup.querySelector(".erj-popup-error");
    if (!errorElement) return;

    errorElement.textContent = "";
    errorElement.style.display = "none";
  };

  // Handle API error and timeout
  const _handleApiError = (
    popupId,
    errorMessage = "Request failed. Please try again later."
  ) => {
    // add loading state
    window._erj_.PopupDOM.setLoading(popupId, false);

    // show error message
    _showError(popupId, errorMessage);

    console.error(`[Newsletter] API error: ${errorMessage}`);
  };

  const _newsletterHandler = {
    // Handle form submission
    handleSubmit(popupId) {
      const store = window._erj_.PopupStore.getPopupStore(popupId);

      if (!store) {
        return;
      }

      // Get the submit button
      const popup = _getPopupEle(popupId);
      const submitButton = popup.querySelector("[data-erj-popup-submit]");

      // Check email before submitting
      const email = store.get().data.email;
      if (email && !_validateEmail(email)) {
        _showError(popupId, ERR_MSG.email_validation);
        return;
      }

      // Add loading state to button
      if (submitButton) {
        submitButton.classList.add("erj-popup-button--loading");
      }

      // Emit submission event
      window._erj_.PopupEventBus.emit("ejr_event:popup_form_submit", {
        popupId,
        type: "newsletter",
        data: store.get().data, // TODO - get data from form
      });

      // Set loading state
      window._erj_.PopupDOM.setLoading(popupId, true);

      // enum FollowUpType {
      //   THANK_YOU_AND_HERE_IS_YOUR_DISCOUNT = 0,
      //   THANK_YOU_FOR_SUBSCRIBING = 1,
      //   JUMP_LOGIN_PAGE = 2,
      //   JUMP_SIGNUP_PAGE = 3,
      // }

      const FOLLOW_UP_TYPE_MAPPING = {
        0: "success",
        1: "success",
        2: "subscribed",
        3: "subscribed",
      };

      // Send actual API request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 20 * 1000); // 20 seconds timeout

      const urlSearchParams = new URLSearchParams(window.location.search);

      fetch("https://api.enroutejewelry.com/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: store.get().data.email,
          isLoggedIn: Boolean(window._erj_.customer.id),
          utm_source: urlSearchParams.get("utm_source") || "",
          utm_medium: urlSearchParams.get("utm_medium") || "",
          utm_campaign: urlSearchParams.get("utm_campaign") || "",
        }),
        mode: "cors",
        signal: controller.signal,
      })
        .then((response) => response.json())
        .then((result) => {
          clearTimeout(timeoutId);
          // Close loading state
          window._erj_.PopupDOM.setLoading(popupId, false);

          // Remove loading state from button
          if (submitButton) {
            submitButton.classList.remove("erj-popup-button--loading");
          }

          if (!result.data || !result.data.success) {
            console.error(
              `[Newsletter] API request failed: ${JSON.stringify(result)}`
            );
            // Show error message when API returns error
            _handleApiError(
              popupId,
              "Subscription failed. Please try again later."
            );
            return;
          }

          gtag('event', 'custom_subscribe_click', {
            'popup_type':'newsletter',
          });

          // Save response type to store
          const followUpType = result.data.followUp;

          // Handle followUp = -1
          if (followUpType === -1) {
            _handleApiError(
              popupId,
              "Subscription failed. Please try again later."
            );
            return;
          }

          const store = window._erj_.PopupStore.getPopupStore(popupId);
          if (store) {
            store.setKey("data", {
              ...store.get().data,
              followUpType: followUpType,
            });
          }

          // Change popup state to step 2 - success
          window._erj_.PopupDOM.setStep(
            popupId,
            2, // TODO - get curr step and update to next step
            FOLLOW_UP_TYPE_MAPPING[followUpType]
          );
        })
        .catch((error) => {
          clearTimeout(timeoutId);
          console.error(`[Newsletter] API request error: ${error}`);

          // Close loading state
          window._erj_.PopupDOM.setLoading(popupId, false);

          // Remove loading state from button
          if (submitButton) {
            submitButton.classList.remove("erj-popup-button--loading");
          }

          // Show error message
          _handleApiError(
            popupId,
            error.name === "AbortError"
              ? ERR_MSG.subscribe_timeout
              : ERR_MSG.subscribe_failed
          );
        });
    },

    // Handle success button click event
    handleResultButtonClick(popupId) {
      const store = window._erj_.PopupStore.getPopupStore(popupId);
      if (!store) {
        return;
      }

      const followUpType = store.get().data.followUpType;

      // Execute different operations based on response type
      switch (parseInt(followUpType)) {
        case 0: // THANK_YOU_AND_HERE_IS_YOUR_DISCOUNT
          // Close popup, display discount code
          window._erj_.PopupQueue.closePopup(popupId, {
            reason: "user_action:click_close_button",
          });
          break;

        case 1: // THANK_YOU_FOR_SUBSCRIBING
          // Just close the popup
          window._erj_.PopupQueue.closePopup(popupId, {
            reason: "user_action:click_close_button",
          });
          break;

        case 2: // JUMP_LOGIN_PAGE
          // Redirect to login page
          window.location.href = "/account/login";
          break;

        case 3: // JUMP_SIGNUP_PAGE
          // Redirect to login page
          window.location.href = "/account/login";
          break;

        default:
          // Default just closes the popup
          window._erj_.PopupQueue.closePopup(popupId, {
            reason: "user_action:click_close_button",
          });
      }
    },

    // Handle input changes
    handleInput(popupId, inputElement) {
      const store = window._erj_.PopupStore.getPopupStore(popupId);
      if (!store) {
        return;
      }

      const popup = _getPopupEle(popupId);
      const button = popup.querySelector(".erj-popup-button");

      const email = inputElement.value;

      // Update store with input value
      store.setKey("data", {
        ...store.get().data,
        email: inputElement.value,
      });

      // Check if email is empty
      if (!email) {
        // If email is empty, hide error message and disable button
        _hideError(popupId);
        button.classList.add("disabled");
        return;
      }

      const isValid = _validateEmail(email);
      if (!isValid) {
        _showError(popupId, ERR_MSG.email_validation);
        button.classList.add("disabled");
      } else {
        _hideError(popupId);
        button.classList.remove("disabled");
      }
    },

    init(popupId) {
      // Get popup element
      const popup = _getPopupEle(popupId);
      if (!popup) return;

      // Get button element
      const button = popup.querySelector(
        '[data-erj-popup-step="1"] [data-erj-popup-submit]'
      );
      if (!button) return;

      // Set button to disabled by default
      button.classList.add("disabled");

      // If logged in, check pre-fill email
      if (window._erj_.customer.id) {
        const store = window._erj_.PopupStore.getPopupStore(popupId);
        if (!store) return;

        const customerEmail = window._erj_.customer.email;

        // sync user email to store
        store.setKey("data", {
          ...store.get().data,
          email: customerEmail,
        });

        // Only validate email if it's not empty
        if (!customerEmail) {
          return;
        }

        // check email is valid
        const isValid = _validateEmail(customerEmail);
        if (!isValid) {
          _showError(popupId, ERR_MSG.email_validation);
        } else {
          // Email is valid, enable button
          button.classList.remove("disabled");
          _hideError(popupId);
        }
      }
    },
  };

  // Export to global object
  window._erj_ = window._erj_ || {};
  window._erj_.PopupHandlers = {
    // Newsletter type handler
    newsletter: _newsletterHandler,
    // Other type handlers can be added in the future
    // product: { ... },
    // promotion: { ... }
  };
})();
